import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  Sparkles,
  History,
  CheckCircle2,
  Trash2,
  Zap,
  Save,
  Wrench,
  Shield
} from "lucide-react";
import api from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import PageHeader from "../components/PageHeader";
import Dropdown from "../components/Dropdown";
import { useToast } from "../context/ToastContext";
import SimpleMdeEditor from "react-simplemde-editor";
import "easymde/dist/easymde.min.css";

const CHANNELS = [
  { id: "widget", name: "Chat Widget" },
  { id: "email", name: "Email" },
  { id: "freshchat", name: "Freshchat" },
  { id: "freshdesk", name: "Freshdesk" },
  { id: "slack", name: "Slack" },
  { id: "telegram", name: "Telegram" },
  { id: "webchat", name: "Web Chat" },
  { id: "webhook", name: "Webhook" },
  { id: "whatsapp", name: "WhatsApp" },
  { id: "zendesk", name: "Zendesk" }
];

const SPECIAL_AT_ITEMS = [
  { id: '__request_human__', name: 'request_human_assistant', label: 'Request Human Assistant', description: 'Escalate conversation to a live human agent', type: 'special' }
];

const PromptVersioning = ({ activeAiAgentId }) => {
  const [selectedChannel, setSelectedChannel] = useState(CHANNELS[0].id);
  const [prompts, setPrompts] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [versionName, setVersionName] = useState("");
  const [saving, setSaving] = useState(false);
  const [improving, setImproving] = useState(false);
  const [activeTab, setActiveTab] = useState("current"); // 'current' or 'history'
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToast();

  const [isFullScreen] = useState(false);

  // Guardrails state
  const [guardrailsEnabled, setGuardrailsEnabled] = useState(false);
  const [guardrailsContent, setGuardrailsContent] = useState("");
  const [savingGuardrails, setSavingGuardrails] = useState(false);

  const handleSaveGuardrails = async (overrideEnabled = null) => {
    if (!activeAiAgentId) return;
    const isEnabled = typeof overrideEnabled === "boolean" ? overrideEnabled : guardrailsEnabled;
    setSavingGuardrails(true);
    try {
      const response = await api.updateGuardrails(
        selectedChannel,
        isEnabled,
        guardrailsContent
      );
      if (response && response.message) {
        showToast(typeof overrideEnabled === "boolean" ? `Guardrails ${overrideEnabled ? "enabled" : "disabled"}` : "Guardrails saved successfully", "success");
        await loadPrompts();
      }
    } catch (error) {
      console.error("Error saving guardrails:", error);
      showToast("Failed to save guardrails", "error");
    } finally {
      setSavingGuardrails(false);
    }
  };

  const loadPrompts = React.useCallback(async () => {
    try {
      const response = await api.getInstructionVersions(selectedChannel);
      const versions = response.versions || [];
      setPrompts(versions);

      const active = versions.find(p => p.is_active);
      if (active) {
        if (activeTab === 'current') {
          setCurrentPrompt(active.content);
        }
        setGuardrailsEnabled(active.guardrails_enabled || false);
        setGuardrailsContent(active.guardrails_content || "");
      } else if (!active && activeTab === 'current') {
        setCurrentPrompt("");
        setGuardrailsEnabled(false);
        setGuardrailsContent("");
      }
    } catch (error) {
      console.error("Error loading prompts:", error);
    }
  }, [activeTab, setCurrentPrompt, selectedChannel]);

  const mdeOptions = React.useMemo(() => ({
    autofocus: false,
    spellChecker: false,
    placeholder: "e.g. You are a helpful assistant for EasyChat...",
    status: false,
    toolbar: [
      "bold", "italic", "strikethrough", "|",
      "heading-1", "heading-2", "heading-3", "|",
      "unordered-list", "ordered-list", "|",
      "code", "quote", "horizontal-rule", "table"
    ]
  }), []);

  // Suggestions state
  const [tools, setTools] = useState([]);
  const [flows, setFlows] = useState([]);
  const [customParams, setCustomParams] = useState([]);
  const [suggestionType, setSuggestionType] = useState(null); // 'tool' | 'param'
  const [filteredItems, setFilteredItems] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [suggestionCoords, setSuggestionCoords] = useState({ top: 0, left: 0 });
  const [editorInstance, setEditorInstance] = useState(null);

  const mdeRef = useRef(null);

  // Refs for tracking state inside CodeMirror event listeners
  const showSuggestionsRef = useRef(showSuggestions);
  const filteredItemsRef = useRef(filteredItems);
  const suggestionIndexRef = useRef(suggestionIndex);
  const suggestionTypeRef = useRef(suggestionType);

  useEffect(() => {
    showSuggestionsRef.current = showSuggestions;
    filteredItemsRef.current = filteredItems;
    suggestionIndexRef.current = suggestionIndex;
    suggestionTypeRef.current = suggestionType;
  }, [showSuggestions, filteredItems, suggestionIndex, suggestionType]);


  const loadActiveTools = React.useCallback(async () => {
    try {
      const response = await api.getTools();
      const toolsArray = Array.isArray(response) ? response : (response?.tools || []);
      const activeTools = toolsArray.filter(t => t.is_active);
      setTools(activeTools);
    } catch (error) {
      console.error("Error loading tools for instructions:", error);
    }
  }, []);

  const loadFlows = React.useCallback(async () => {
    try {
      const response = await api.getFlows();
      const flowsArray = Array.isArray(response) ? response : (response?.flows || []);
      setFlows(flowsArray);
    } catch (error) {
      console.error("Error loading business flows for instructions:", error);
    }
  }, []);

  const loadCustomParameters = React.useCallback(async () => {
    try {
      const response = await api.getWidgetConfig();
      const params = (response && response.custom_parameters) || [];
      setCustomParams(params);
    } catch (error) {
      console.error("Error loading custom parameters for instructions:", error);
      setCustomParams([]);
    }
  }, []);

  useEffect(() => {
    const checkActiveChannels = async () => {
      try {
        const activeChannels = await api.getActiveInstructionChannels();
        if (activeChannels && activeChannels.length === 1) {
          const activeChan = activeChannels[0];
          const exists = CHANNELS.some(c => c.id === activeChan);
          if (exists) {
            setSelectedChannel(activeChan);
          }
        }
      } catch (error) {
        console.error("Error fetching active channels:", error);
      }
    };
    checkActiveChannels();
  }, []);

  useEffect(() => {
    loadPrompts();
    loadActiveTools();
    loadFlows();
    loadCustomParameters();
  }, [loadPrompts, loadActiveTools, loadFlows, loadCustomParameters]);

  useEffect(() => {
    if (showSuggestions) {
      if (suggestionType === "tool") {
        const q = suggestionQuery.toLowerCase();
        const allAtItems = [
          ...SPECIAL_AT_ITEMS,
          ...flows.map(f => ({ id: `flow_${f.id}`, name: f.name, label: f.name, description: f.description || 'Business flow', type: 'flow' })),
          ...tools.map(t => ({ ...t, type: 'tool' }))
        ];
        const filtered = allAtItems.filter(item =>
          item.name.toLowerCase().includes(q) ||
          (item.label || '').toLowerCase().includes(q)
        );
        setFilteredItems(filtered);
        setSuggestionIndex((prevIndex) => (prevIndex >= filtered.length ? 0 : prevIndex));
      } else if (suggestionType === "param") {
        const inbuiltParams = ["name", "email", "phone", "userid"];
        const allParams = Array.from(new Set([...inbuiltParams, ...customParams]));
        const filtered = allParams.filter(param =>
          param.toLowerCase().includes(suggestionQuery.toLowerCase())
        );
        setFilteredItems(filtered);
        setSuggestionIndex((prevIndex) => (prevIndex >= filtered.length ? 0 : prevIndex));
      }
    } else {
      setFilteredItems([]);
    }
  }, [suggestionQuery, showSuggestions, tools, flows, customParams, suggestionType, setFilteredItems, setSuggestionIndex]);

  // Recalculate suggestions box coordinates dynamically based on actual filtered item count height and scroll events
  useEffect(() => {
    if (!showSuggestions || !editorInstance || filteredItems.length === 0) return;

    const updateCoords = () => {
      const cm = editorInstance.codemirror;
      const coords = cm.cursorCoords(true, "window");
      const estimatedHeight = Math.min(filteredItemsRef.current.length * 45 + 10, 240);
      const spaceBelow = window.innerHeight - coords.bottom;
      const showAbove = spaceBelow < estimatedHeight && coords.top > estimatedHeight;

      setSuggestionCoords({
        top: showAbove ? coords.top - estimatedHeight - 5 : coords.bottom + 5,
        left: coords.left
      });
    };

    updateCoords();

    // Listen to scroll events on window or any scrollable container (capturing phase)
    window.addEventListener("scroll", updateCoords, true);
    return () => window.removeEventListener("scroll", updateCoords, true);
  }, [filteredItems, showSuggestions, editorInstance]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.EasyMDEContainer') && !e.target.closest('.suggestion-box')) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleCreatePrompt = async () => {
    if (!currentPrompt.trim()) return;
    if (prompts.length >= 5) {
      showToast("You have reached the limit of 5 prompts. Please delete an older version to add a new one.", "warning");
      return;
    }

    setSaving(true);
    try {
      const name = versionName.trim();
      await api.createInstructions(currentPrompt, name, selectedChannel);
      setVersionName("");
      await loadPrompts();
      setActiveTab("history");
      showToast("New prompt version created and activated!", "success");
    } catch (error) {
      showToast(error.message || "Failed to create prompt version", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    if (currentActive) return; // Already is_active

    try {
      await api.activateInstruction(id);
      await loadPrompts();
      showToast("Instruction activated successfully", "success");
    } catch (error) {
      showToast(error.message || "Failed to activate prompt", "error");
    }
  };

  const handleDelete = (id, isActive) => {
    if (isActive) {
      showToast("Cannot delete the currently active prompt. Please activate another one first.", "error");
      return;
    }
    setPromptToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!promptToDelete) return;
    setIsDeleting(true);
    try {
      await api.deleteInstruction(promptToDelete);
      await loadPrompts();
      setIsDeleteModalOpen(false);
      showToast("Prompt version deleted successfully", "success");
    } catch (error) {
      showToast(error.message || "Failed to delete prompt", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleImprove = async () => {
    if (!currentPrompt.trim()) return;
    setImproving(true);
    try {
      const response = await api.improvePrompt(currentPrompt);
      if (response.success) {
        setCurrentPrompt(response.improved_prompt);
      }
    } catch (error) {
      showToast("Failed to improve prompt", "error");
    } finally {
      setImproving(false);
    }
  };

  const handleEditorActivity = (cm) => {
    const doc = cm.getDoc();
    const cursor = doc.getCursor();
    const lineText = doc.getLine(cursor.line) || "";
    const textBeforeCursor = lineText.substring(0, cursor.ch);

    const lastAt = textBeforeCursor.lastIndexOf("@");
    const lastParam = textBeforeCursor.lastIndexOf("{{");

    if (lastAt !== -1 && lastAt > lastParam && (lastAt === 0 || /\s/.test(textBeforeCursor.charAt(lastAt - 1)))) {
      const query = textBeforeCursor.substring(lastAt + 1);
      if (!/\s/.test(query)) {
        setSuggestionQuery(query);
        setSuggestionType("tool");
        setShowSuggestions(true);

        const coords = cm.cursorCoords(true, "window");
        setSuggestionCoords({
          top: coords.bottom + 5,
          left: coords.left
        });
        return;
      }
    } else if (lastParam !== -1 && lastParam > lastAt && (lastParam === 0 || /\s/.test(textBeforeCursor.charAt(lastParam - 1)))) {
      const query = textBeforeCursor.substring(lastParam + 2);
      if (!/\s/.test(query) && !query.includes("}")) {
        setSuggestionQuery(query);
        setSuggestionType("param");
        setShowSuggestions(true);

        const coords = cm.cursorCoords(true, "window");
        setSuggestionCoords({
          top: coords.bottom + 5,
          left: coords.left
        });
        return;
      }
    }

    setShowSuggestions(false);
    setSuggestionType(null);
  };

  const handleEditorKeydown = (cm, e) => {
    if (!showSuggestionsRef.current || filteredItemsRef.current.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestionIndex((prev) => (prev + 1) % filteredItemsRef.current.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSuggestionIndex((prev) => (prev - 1 + filteredItemsRef.current.length) % filteredItemsRef.current.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      selectItem(filteredItemsRef.current[suggestionIndexRef.current]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setShowSuggestions(false);
    }
  };

  const selectItem = (item) => {
    if (!editorInstance) return;
    const cm = editorInstance.codemirror;
    const doc = cm.getDoc();
    const cursor = doc.getCursor();
    const lineText = doc.getLine(cursor.line) || "";
    const textBeforeCursor = lineText.substring(0, cursor.ch);

    if (suggestionTypeRef.current === "tool") {
      const atIndex = textBeforeCursor.lastIndexOf("@");
      if (atIndex !== -1) {
        const from = { line: cursor.line, ch: atIndex };
        const to = { line: cursor.line, ch: cursor.ch };
        let completedText;
        if (item.type === 'flow') {
          completedText = `**business_flow_loader(flow_name='${item.name}')** `;
        } else if (item.name === 'request_human_assistant') {
          completedText = `**request_human_assistant(reason='User requested human assistance')** `;
        } else {
          completedText = `**${item.name}** `;
        }
        doc.replaceRange(completedText, from, to);
        setShowSuggestions(false);
        setTimeout(() => {
          cm.focus();
          // Force update markings after insertion
          if (typeof triggerMarkUpdate === 'function') {
            triggerMarkUpdate();
          }
        }, 0);
      }
    } else if (suggestionTypeRef.current === "param") {
      const lastBraceIndex = textBeforeCursor.lastIndexOf("{{");
      if (lastBraceIndex !== -1) {
        const from = { line: cursor.line, ch: lastBraceIndex };
        const to = { line: cursor.line, ch: cursor.ch };
        const completedText = `{{${item}}} `;
        doc.replaceRange(completedText, from, to);
        setShowSuggestions(false);
        setTimeout(() => cm.focus(), 0);
      }
    }
  };

  // Bind events to CodeMirror instance
  const activityHandlerRef = useRef();
  const keydownHandlerRef = useRef();

  activityHandlerRef.current = () => {
    if (editorInstance) {
      handleEditorActivity(editorInstance.codemirror);
    }
  };
  keydownHandlerRef.current = (cm, e) => {
    handleEditorKeydown(cm, e);
  };

  // Tooltip hover states and markings logic
  const [hoveredFlow, setHoveredFlow] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const markedRangesRef = useRef([]);

  const flowsRef = useRef(flows);
  useEffect(() => {
    flowsRef.current = flows;
  }, [flows]);

  const triggerMarkUpdate = React.useCallback(() => {
    if (!editorInstance) return;
    const cm = editorInstance.codemirror;
    const doc = cm.getDoc();

    // Clear previous marks
    markedRangesRef.current.forEach(mark => mark.clear());
    markedRangesRef.current = [];

    const totalLines = doc.lineCount();
    for (let i = 0; i < totalLines; i++) {
      const text = doc.getLine(i);

      // 1. Scan for **business_flow_loader(flow_name='FlowName')**
      const flowRegex = /\*\*business_flow_loader\(flow_name='([^']+)'\)\*\*/g;
      let match;
      while ((match = flowRegex.exec(text)) !== null) {
        const startCh = match.index;
        const endCh = match.index + match[0].length;
        const flowName = match[1];

        const matchedFlow = flowsRef.current.find(f => f.name.toLowerCase() === flowName.toLowerCase());
        const keywords = matchedFlow ? (matchedFlow.keywords || matchedFlow.parameters || []) : [];
        const description = matchedFlow ? (matchedFlow.description || "") : "";
        const keywordsStr = Array.isArray(keywords) ? keywords.join(", ") : String(keywords);

        // Build widget showing only the function name
        const widget = document.createElement("span");
        widget.className = "flow-chip-cm";
        widget.textContent = "business_flow_loader";
        widget.setAttribute("data-flowname", flowName);
        widget.setAttribute("data-type", "flow");
        widget.setAttribute("data-keywords", keywordsStr);
        widget.setAttribute("data-desc", description);

        const mark = cm.markText(
          { line: i, ch: startCh },
          { line: i, ch: endCh },
          { replacedWith: widget, handleMouseEvents: true }
        );
        markedRangesRef.current.push(mark);
      }

      // 2. Scan for **request_human_assistant(reason='...')**
      const handoffRegex = /\*\*request_human_assistant\(([^)]*)\)\*\*/g;
      let handoffMatch;
      while ((handoffMatch = handoffRegex.exec(text)) !== null) {
        const startCh = handoffMatch.index;
        const endCh = handoffMatch.index + handoffMatch[0].length;
        const rawArgs = handoffMatch[1]; // e.g. reason='User requested human assistance'

        // Extract actual reason value if present
        const reasonMatch = rawArgs.match(/reason=['"]([^'"]+)['"]/);
        const reasonValue = reasonMatch ? reasonMatch[1] : null;
        const paramsDisplay = reasonValue
          ? `reason='${reasonValue}'`
          : (rawArgs.trim() || "reason (string)");

        const widget = document.createElement("span");
        widget.className = "tool-chip-cm";
        widget.textContent = "request_human_assistant";
        widget.setAttribute("data-toolname", "request_human_assistant");
        widget.setAttribute("data-type", "tool");
        widget.setAttribute("data-params", paramsDisplay);
        widget.setAttribute("data-desc", "Escalate conversation to a live human agent");

        const mark = cm.markText(
          { line: i, ch: startCh },
          { line: i, ch: endCh },
          { replacedWith: widget, handleMouseEvents: true }
        );
        markedRangesRef.current.push(mark);
      }

      // 3. Scan for other API tool chips: **ToolName** (simple bold, no parens)
      const toolRegex = /\*\*([a-zA-Z0-9_]+)\*\*/g;
      let toolMatch;
      while ((toolMatch = toolRegex.exec(text)) !== null) {
        const startCh = toolMatch.index;
        const endCh = toolMatch.index + toolMatch[0].length;
        const toolName = toolMatch[1];

        // Skip business_flow_loader and request_human_assistant (handled above)
        if (toolName === "business_flow_loader" || toolName === "request_human_assistant") continue;

        const matchedTool = tools.find(t => t.name.toLowerCase() === toolName.toLowerCase());
        if (!matchedTool) continue;

        const description = matchedTool.description || "";
        const parameters = matchedTool.parameters || matchedTool.properties || "No parameters";

        const widget = document.createElement("span");
        widget.className = "tool-chip-cm";
        widget.textContent = toolName;
        widget.setAttribute("data-toolname", toolName);
        widget.setAttribute("data-type", "tool");
        widget.setAttribute("data-params", typeof parameters === "object" ? JSON.stringify(parameters) : String(parameters));
        widget.setAttribute("data-desc", description);

        const mark = cm.markText(
          { line: i, ch: startCh },
          { line: i, ch: endCh },
          { replacedWith: widget, handleMouseEvents: true }
        );
        markedRangesRef.current.push(mark);
      }

      // 4. Scan for {{variable}} patterns
      const varRegex = /\{\{([a-zA-Z0-9_]+)\}\}/g;
      let varMatch;
      while ((varMatch = varRegex.exec(text)) !== null) {
        const startCh = varMatch.index;
        const endCh = varMatch.index + varMatch[0].length;
        const varName = varMatch[1];

        const widget = document.createElement("span");
        widget.className = "var-chip-cm";
        widget.textContent = varName;
        widget.setAttribute("data-varname", varName);
        widget.setAttribute("data-type", "variable");

        const mark = cm.markText(
          { line: i, ch: startCh },
          { line: i, ch: endCh },
          { replacedWith: widget, handleMouseEvents: true }
        );
        markedRangesRef.current.push(mark);
      }
    }
  }, [editorInstance, tools]);

  // Handle hover tooltips on flow and tool chips
  useEffect(() => {
    if (!editorInstance) return;
    const cm = editorInstance.codemirror;
    const wrapper = cm.getWrapperElement();

    const handleMouseOver = (e) => {
      const flowTarget = e.target.closest(".flow-chip-cm");
      const toolTarget = e.target.closest(".tool-chip-cm");
      const varTarget = e.target.closest(".var-chip-cm");

      if (flowTarget) {
        const flowName = flowTarget.getAttribute("data-flowname");
        const keywords = flowTarget.getAttribute("data-keywords");
        const desc = flowTarget.getAttribute("data-desc");
        const rect = flowTarget.getBoundingClientRect();
        setHoveredFlow({
          name: flowName,
          type: "flow",
          keywords: keywords || "No parameters specified",
          description: desc
        });
        setTooltipPos({
          top: rect.bottom + 6,
          left: rect.left
        });
      } else if (toolTarget) {
        const toolName = toolTarget.getAttribute("data-toolname");
        const params = toolTarget.getAttribute("data-params");
        const desc = toolTarget.getAttribute("data-desc");
        const rect = toolTarget.getBoundingClientRect();

        let displayParams = params;
        try {
          const parsed = JSON.parse(params);
          if (typeof parsed === "object") {
            displayParams = Object.keys(parsed).join(", ");
          }
        } catch (err) { }

        setHoveredFlow({
          name: toolName,
          type: "tool",
          keywords: displayParams || "No parameters specified",
          description: desc
        });
        setTooltipPos({
          top: rect.bottom + 6,
          left: rect.left
        });
      } else if (varTarget) {
        const varName = varTarget.getAttribute("data-varname");
        const rect = varTarget.getBoundingClientRect();
        setHoveredFlow({
          name: varName,
          type: "variable",
          keywords: `{{${varName}}}`,
          description: `Dynamic variable — will be replaced with the user's ${varName} value at runtime.`
        });
        setTooltipPos({
          top: rect.bottom + 6,
          left: rect.left
        });
      } else {
        setHoveredFlow(null);
      }
    };

    const handleMouseLeave = (e) => {
      if (!e.relatedTarget || (!e.relatedTarget.closest(".flow-chip-cm") && !e.relatedTarget.closest(".tool-chip-cm") && !e.relatedTarget.closest(".var-chip-cm"))) {
        setHoveredFlow(null);
      }
    };

    wrapper.addEventListener("mouseover", handleMouseOver);
    wrapper.addEventListener("mouseout", handleMouseLeave);

    // Update marks initially and on prompt changes
    triggerMarkUpdate();
    const changeListener = () => {
      setTimeout(triggerMarkUpdate, 10);
    };
    cm.on("change", changeListener);

    return () => {
      wrapper.removeEventListener("mouseover", handleMouseOver);
      wrapper.removeEventListener("mouseout", handleMouseLeave);
      cm.off("change", changeListener);
      markedRangesRef.current.forEach(mark => mark.clear());
    };
  }, [editorInstance, flows, tools, triggerMarkUpdate]);

  useEffect(() => {
    if (!editorInstance) return;
    const cm = editorInstance.codemirror;

    const activityListener = () => activityHandlerRef.current();
    const keydownListener = (cmInst, e) => keydownHandlerRef.current(cmInst, e);

    cm.on("cursorActivity", activityListener);
    cm.on("keydown", keydownListener);

    return () => {
      cm.off("cursorActivity", activityListener);
      cm.off("keydown", keydownListener);
    };
  }, [editorInstance]);

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn border-t border-gray-100">
      <div className="w-full max-w-6xl mx-auto space-y-6">
        <PageHeader
          title="Neural Directives"
          description="Orchestrate the personality and operational parameters of your digital agent with precision."
        />

        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-4 bg-white/40 backdrop-blur-md p-4 rounded-3xl border border-gray-100/50 shadow-sm relative z-10">
          {/* Left Column (Empty for alignment) */}
          <div className="hidden md:block"></div>

          {/* Center Column (Tabs) */}
          <div className="flex justify-center">
            <div className="flex bg-white/50 p-1 rounded-2xl border border-gray-100/50 shadow-inner w-fit">
              <button
                onClick={() => setActiveTab("current")}
                className={`px-6 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all duration-300 flex items-center gap-2 ${activeTab === 'current'
                    ? 'bg-gray-900 text-white shadow-lg shadow-gray-200'
                    : 'text-gray-400 hover:text-gray-900 hover:bg-white'
                  }`}
              >
                <Zap size={14} /> Editor
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`px-6 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all duration-300 flex items-center gap-2 ${activeTab === 'history'
                    ? 'bg-gray-900 text-white shadow-lg shadow-gray-200'
                    : 'text-gray-400 hover:text-gray-900 hover:bg-white'
                  }`}
              >
                <History size={14} /> History
                <span className={`px-2 py-0.5 rounded-full text-[9px] ml-1 font-black ${activeTab === 'history' ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-400'}`}>
                  {prompts.length}/5
                </span>
              </button>
            </div>
          </div>

          {/* Right Column (Channel Dropdown) */}
          <div className="flex items-center justify-end gap-3 w-full md:w-auto">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Channel:</span>
            <Dropdown
              options={CHANNELS}
              value={selectedChannel}
              onChange={setSelectedChannel}
              className="min-w-[200px]"
            />
          </div>
        </div>

        {activeTab === 'current' ? (
          <div className="w-full">
            <div className={`bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 flex flex-col justify-between transition-all duration-300 ${isFullScreen
                ? "fixed inset-4 z-[9999] p-8 md:p-10 shadow-2xl"
                : "p-8 relative"
              }`}>

              {/* Header and Enhancer */}
              <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">Agent instructions</h3>
                  <p className="text-[11px] font-medium text-gray-400">
                    Provide structured instructions, ensuring each step guides the user through the conversation.
                  </p>
                </div>
                <button
                  onClick={handleImprove}
                  disabled={improving || !currentPrompt.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50 text-[10px] font-black uppercase tracking-wider border border-emerald-200/50 rounded-xl"
                >
                  <Sparkles size={12} className={improving ? "animate-spin" : ""} />
                  {improving ? "Refining..." : "Enhance with AI"}
                </button>
              </div>



              {/* Markdown rich-text editor */}
              <div className="relative text-left">
                <style>{`
                  .EasyMDEContainer {
                    width: 100%;
                  }
                  .editor-toolbar {
                    border: 1px solid #f3f4f6 !important;
                    border-top-left-radius: 16px !important;
                    border-top-right-radius: 16px !important;
                    background-color: #f9fafb !important;
                    padding: 8px 16px !important;
                  }
                  .editor-toolbar button {
                    border-radius: 8px !important;
                    transition: all 0.2s !important;
                  }
                  .editor-toolbar button.active, .editor-toolbar button:hover {
                    background-color: #e5e7eb !important;
                  }
                  .CodeMirror {
                    border: 1px solid #f3f4f6 !important;
                    border-top: none !important;
                    border-bottom-left-radius: 16px !important;
                    border-bottom-right-radius: 16px !important;
                    font-family: Inter, sans-serif !important;
                    font-size: 14px !important;
                    line-height: 1.6 !important;
                    padding: 12px !important;
                    min-height: 350px !important;
                    background-color: #ffffff !important;
                  }
                  .CodeMirror .cm-header-1 {
                    font-size: 1.5rem !important;
                    line-height: 1.4 !important;
                  }
                  .CodeMirror .cm-header-2 {
                    font-size: 1.3rem !important;
                    line-height: 1.4 !important;
                  }
                  .CodeMirror .cm-header-3 {
                    font-size: 1.15rem !important;
                    line-height: 1.4 !important;
                  }
                  .editor-preview-active-side {
                    border-bottom-left-radius: 16px !important;
                    border-bottom-right-radius: 16px !important;
                  }
                  .flow-chip-cm {
                    background: #f5f3ff !important;
                    color: #7c3aed !important;
                    border: 1px solid #ddd6fe !important;
                    border-radius: 6px !important;
                    padding: 1px 7px 1px 5px !important;
                    font-weight: 700 !important;
                    font-size: 11.5px !important;
                    cursor: help !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    vertical-align: middle !important;
                    line-height: 20px !important;
                    box-shadow: 0 1px 3px rgba(124, 58, 237, 0.1) !important;
                    margin: 0 3px !important;
                    font-family: Inter, sans-serif !important;
                    user-select: none !important;
                    transition: filter 0.15s !important;
                  }
                  .flow-chip-cm::before {
                    content: '⚡ ' !important;
                    font-size: 10px !important;
                    margin-right: 2px !important;
                    opacity: 0.8 !important;
                  }
                  .flow-chip-cm:hover {
                    filter: brightness(0.92) !important;
                  }
                  .tool-chip-cm {
                    background: #eff6ff !important;
                    color: #2563eb !important;
                    border: 1px solid #bfdbfe !important;
                    border-radius: 6px !important;
                    padding: 1px 7px 1px 5px !important;
                    font-weight: 700 !important;
                    font-size: 11.5px !important;
                    cursor: help !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    vertical-align: middle !important;
                    line-height: 20px !important;
                    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.1) !important;
                    margin: 0 3px !important;
                    font-family: Inter, sans-serif !important;
                    user-select: none !important;
                    transition: filter 0.15s !important;
                  }
                  .tool-chip-cm::before {
                    content: '🔧 ' !important;
                    font-size: 10px !important;
                    margin-right: 2px !important;
                    opacity: 0.8 !important;
                  }
                  .tool-chip-cm:hover {
                    filter: brightness(0.92) !important;
                  }
                  .var-chip-cm {
                    background: #ecfdf5 !important;
                    color: #059669 !important;
                    border: 1px solid #6ee7b7 !important;
                    border-radius: 6px !important;
                    padding: 1px 7px 1px 5px !important;
                    font-weight: 700 !important;
                    font-size: 11.5px !important;
                    cursor: help !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    vertical-align: middle !important;
                    line-height: 20px !important;
                    box-shadow: 0 1px 3px rgba(5, 150, 105, 0.12) !important;
                    margin: 0 3px !important;
                    font-family: monospace !important;
                    user-select: none !important;
                    transition: filter 0.15s !important;
                  }
                  .var-chip-cm::before {
                    content: '{{ ' !important;
                    font-size: 10px !important;
                    opacity: 0.6 !important;
                    font-family: monospace !important;
                  }
                  .var-chip-cm::after {
                    content: ' }}' !important;
                    font-size: 10px !important;
                    opacity: 0.6 !important;
                    font-family: monospace !important;
                  }
                  .var-chip-cm:hover {
                    filter: brightness(0.92) !important;
                  }
                `}</style>
                <SimpleMdeEditor
                  value={currentPrompt}
                  onChange={setCurrentPrompt}
                  options={mdeOptions}
                  getMdeInstance={(instance) => {
                    mdeRef.current = instance;
                    setEditorInstance(instance);
                  }}
                />

                {/* Floating parameters tooltip */}
                {hoveredFlow && ReactDOM.createPortal(
                  <div
                    className="fixed z-[10001] bg-gray-950 text-white rounded-xl p-3 shadow-xl max-w-xs text-xs space-y-1.5 animate-fadeIn border border-gray-800"
                    style={{
                      top: `${tooltipPos.top}px`,
                      left: `${tooltipPos.left}px`,
                    }}
                  >
                    <div className={`font-bold flex items-center gap-1.5 ${hoveredFlow.type === "flow" ? "text-violet-400" : hoveredFlow.type === "variable" ? "text-emerald-400" : "text-blue-400"}`}>
                      {hoveredFlow.type === "flow" ? (
                        <Zap size={12} className="fill-violet-400" />
                      ) : hoveredFlow.type === "variable" ? null : (
                        <Wrench size={12} className="text-blue-400" />
                      )}
                      <span className="font-mono text-[11px]">
                        {hoveredFlow.type === "flow"
                          ? `business_flow_loader(flow_name='${hoveredFlow.name}')`
                          : hoveredFlow.type === "variable"
                            ? `{{${hoveredFlow.name}}}`
                            : hoveredFlow.keywords && hoveredFlow.keywords !== "No parameters specified"
                              ? `${hoveredFlow.name}(${hoveredFlow.keywords})`
                              : hoveredFlow.name
                        }
                      </span>
                    </div>
                    {hoveredFlow.description && (
                      <p className="text-[10px] text-gray-400 leading-normal">{hoveredFlow.description}</p>
                    )}
                    <div className="pt-1 border-t border-gray-800 flex flex-col gap-0.5">
                      <span className="text-[9px] font-black uppercase tracking-wider text-gray-500">
                        {hoveredFlow.type === "flow" ? "Parameters / Keywords:" : hoveredFlow.type === "variable" ? "Variable Syntax:" : "Parameters / Schema:"}
                      </span>
                      <span className="text-gray-200 font-mono text-[10px] leading-relaxed bg-gray-900/50 px-1.5 py-0.5 rounded border border-gray-800/45 w-fit max-w-full truncate">{hoveredFlow.keywords}</span>
                    </div>
                  </div>,
                  document.body
                )}

                {showSuggestions && filteredItems.length > 0 && ReactDOM.createPortal(
                  <div
                    className="suggestion-box fixed z-[10000] bg-white border border-gray-100 rounded-2xl shadow-2xl max-h-60 overflow-y-auto w-64 divide-y divide-gray-50/50 backdrop-blur-md bg-white/95"
                    style={{
                      top: `${suggestionCoords.top}px`,
                      left: `${suggestionCoords.left}px`,
                    }}
                  >
                    <div className="p-1.5 space-y-0.5">
                      {filteredItems.map((item, idx) => (
                        <button
                          key={suggestionType === "tool" ? item.id : item}
                          onClick={() => selectItem(item)}
                          className={`w-full text-left px-3 py-2 text-xs flex flex-col gap-0.5 transition-all duration-200 rounded-xl ${idx === suggestionIndex
                              ? "bg-gray-950 text-white font-bold shadow-lg shadow-gray-200"
                              : "text-gray-600 hover:bg-gray-50"
                            }`}
                        >
                          {suggestionType === "tool" ? (
                            <>
                              <div className="flex items-center gap-1.5 font-bold">
                                {/* Icon by type */}
                                {item.type === 'special' && (
                                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md ${idx === suggestionIndex ? 'bg-rose-900 text-rose-200' : 'bg-rose-50 text-rose-600'
                                    }`}>HUMAN</span>
                                )}
                                {item.type === 'flow' && (
                                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md ${idx === suggestionIndex ? 'bg-violet-900 text-violet-200' : 'bg-violet-50 text-violet-600'
                                    }`}>FLOW</span>
                                )}
                                {item.type === 'tool' && (
                                  <span className={idx === suggestionIndex ? 'text-blue-300' : 'text-blue-600'}>@</span>
                                )}
                                <span>{item.label || item.name}</span>
                              </div>
                              {item.description && (
                                <div className={`text-[10px] font-normal truncate max-w-full ${idx === suggestionIndex ? 'text-gray-300' : 'text-gray-400'
                                  }`}>
                                  {item.description}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center gap-1.5 font-bold">
                              <span className={idx === suggestionIndex ? "text-indigo-300" : "text-indigo-500"}>{"{{"}</span>
                              <span>{item}</span>
                              <span className={idx === suggestionIndex ? "text-indigo-300" : "text-indigo-500"}>{"}}"}</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>,
                  document.body
                )}
              </div>

              <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 w-full">
                <div className="w-full md:flex-1 group">
                  <input
                    type="text"
                    value={versionName}
                    onChange={(e) => setVersionName(e.target.value)}
                    placeholder="Version name (e.g. V2 - Friendly)"
                    className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-gray-900 shadow-inner"
                  />
                </div>
                <button
                  onClick={handleCreatePrompt}
                  disabled={saving || !currentPrompt.trim() || !versionName.trim()}
                  className="flex items-center justify-center gap-2 px-10 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-xs uppercase tracking-widest shadow-md shadow-gray-200 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 flex-shrink-0"
                >
                  <Save size={16} />
                  {saving ? "Saving..." : "Save & Activate"}
                </button>
              </div>
            </div>

            {/* Guardrails Configuration Card */}
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 mt-6 relative overflow-hidden">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                    <Shield size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">Custom Guardrails</h3>
                    <p className="text-[11px] font-medium text-gray-400">Enforce strict guidelines and behavioral boundaries for this agent.</p>
                  </div>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={guardrailsEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setGuardrailsEnabled(checked);
                      handleSaveGuardrails(checked);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
                </label>
              </div>

              {guardrailsEnabled && (
                <div className="space-y-4 animate-fadeIn">
                  <textarea
                    rows={4}
                    value={guardrailsContent}
                    onChange={(e) => setGuardrailsContent(e.target.value)}
                    placeholder="Define guidelines... (e.g. Do not talk about competitors, Do not provide financial advice)"
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 text-sm transition-all"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveGuardrails}
                      disabled={savingGuardrails}
                      className="flex items-center justify-center gap-2 px-8 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-xs uppercase tracking-widest shadow-md shadow-gray-200 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95"
                    >
                      <Save size={14} />
                      {savingGuardrails ? "Saving..." : "Save Guardrails"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="w-full animate-slideIn">
            <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 overflow-hidden">
              <div className="p-6 border-b border-gray-50 bg-gray-50/50 flex items-center justify-between">
                <h3 className="font-bold text-gray-900">Prompt History</h3>
                <span className="text-[11px] font-medium text-gray-400">{prompts.length} Versions Saved</span>
              </div>

              <div className="divide-y divide-gray-50">
                {prompts.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">
                    <History size={48} className="mx-auto mb-4 opacity-20" />
                    <p>No versions saved yet.</p>
                  </div>
                ) : (
                  prompts.map((p) => (
                    <div key={p.id} className={`p-6 flex items-center justify-between gap-6 transition-colors ${p.is_active ? 'bg-blue-50/30' : 'hover:bg-gray-50/50'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-bold text-gray-900 truncate">{p.version_name}</h4>
                          {p.is_active && (
                            <span className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                              <CheckCircle2 size={10} /> Active
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] font-medium text-gray-400 truncate mr-12 font-mono">
                          {p.content}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-2">
                          Last updated: {new Date(p.updated_at).toLocaleString()}
                        </p>
                      </div>

                      <div className="flex items-center gap-4">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={p.is_active}
                            onChange={() => handleToggleActive(p.id, p.is_active)}
                            disabled={p.is_active}
                            className="sr-only peer"
                          />
                          <div className={`w-11 h-6 bg-white border-2 rounded-full peer transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 ${p.is_active
                              ? "border-[#83bbe1] after:translate-x-5 after:bg-[#83bbe1] cursor-default"
                              : "border-gray-200 after:bg-gray-300 hover:border-gray-300 cursor-pointer"
                            }`}></div>
                        </label>
                        {p.is_active ? (
                          <div
                            className="flex items-center justify-center p-2.5 rounded-xl border bg-gray-50 text-gray-400 border-gray-100 cursor-help"
                            title="Active Prompt — protected from deletion"
                          >
                            <Shield size={18} />
                          </div>
                        ) : (
                          <button
                            onClick={() => handleDelete(p.id, p.is_active)}
                            className="p-2.5 rounded-xl border transition-all shadow-sm flex items-center justify-center bg-white border-gray-100 text-gray-400 hover:text-red-500 hover:bg-red-50 hover:border-red-100"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <p className="mt-6 text-center text-xs text-gray-400">
              Only the active prompt governs the AI agent's behavior. Max 5 versions allowed.
            </p>
          </div>
        )}
      </div>
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Delete Neural Directive?"
        message="This action will permanently Delete this instruction sequence from the neural history. Are you prepared to proceed with the deletion?"
      />
    </div>
  );
};

export default PromptVersioning;
